/**
 * Trainer Session Topics - Meeting Tools sidebar panel.
 * Reuses live topic progress (local complete + LiveKit clock sync).
 */

import React, { useMemo } from "react";
import { Check, Circle } from "lucide-react";
import type { SessionTopicPlan } from "./topicProgress";
import {
  buildTopicSegments,
  formatActualDurationDisplay,
  formatTopicElapsedLabel,
  resolveActualDurationSec,
} from "./topicProgress";

type Props = {
  topics: SessionTopicPlan[];
  currentTopic?: SessionTopicPlan;
  topicProgressPercent: number;
  topicElapsedLabel: string;
  topicTimeLimitSec: number;
  onMarkCompleted: () => void;
  currentSubtopicName?: string | null;
  subtopicElapsedLabel?: string;
  subtopicProgressPercent?: number;
  subtopicTimeLimitSec?: number;
};

function statusBadge(
  status: "completed" | "skipped" | "current" | "pending",
  index: number,
) {
  const label =
    status === "completed"
      ? "COMPLETED"
      : status === "skipped"
        ? "SKIPPED"
        : status === "current"
          ? "CURRENT"
          : "PENDING";
  const bg =
    status === "completed"
      ? "rgba(34,197,94,0.18)"
      : status === "skipped"
        ? "rgba(245,158,11,0.18)"
        : status === "current"
          ? "rgba(59,130,246,0.2)"
          : "rgba(148,163,184,0.15)";
  const color =
    status === "completed"
      ? "#4ade80"
      : status === "skipped"
        ? "#fbbf24"
        : status === "current"
          ? "#60a5fa"
          : "#94a3b8";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 6,
        padding: "3px 8px",
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.4,
        background: bg,
        color,
      }}
    >
      #{index + 1} {label}
    </span>
  );
}

export function SessionTopicsPage({
  topics,
  currentTopic,
  topicProgressPercent,
  topicElapsedLabel,
  topicTimeLimitSec,
  onMarkCompleted,
  currentSubtopicName,
  subtopicElapsedLabel,
  subtopicProgressPercent = 0,
  subtopicTimeLimitSec = 0,
}: Props) {
  // Segments only for per-card status (current / completed / pending).
  // Do NOT render the full session bar inside one topic card.
  const segments = useMemo(
    () =>
      buildTopicSegments(
        topics,
        currentTopic,
        topicProgressPercent,
        topicElapsedLabel,
      ),
    [topics, currentTopic, topicProgressPercent, topicElapsedLabel],
  );

  // Flatten for segment counts: subtopic-level when subtopics exist
  const flatItems = useMemo(() => {
    const flat: Array<{completed: boolean; skipped?: boolean}> = [];
    for (const t of topics) {
      if (t.subtopics && t.subtopics.length > 0) {
        for (const s of t.subtopics) flat.push({ completed: s.completed });
      } else {
        flat.push({ completed: t.completed, skipped: t.skipped });
      }
    }
    return flat;
  }, [topics]);
  const doneCount = flatItems.filter((t) => t.completed || t.skipped).length;
  const skippedCount = flatItems.filter((t) => t.skipped).length;
  const totalCount = flatItems.length;
  const plannedMin = topics.reduce((sum, t) => sum + (t.durationMin || 0), 0);
  const limitLabel = formatTopicElapsedLabel(Math.max(0, topicTimeLimitSec));
  const currentFill = Math.max(0, Math.min(100, Number(topicProgressPercent) || 0));
  const activeTopicName = currentTopic?.name || "Topic";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 0,
        height: "100%",
        color: "#e2e8f0",
      }}
    >
      <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
        {doneCount}/{totalCount} done
        {skippedCount > 0 ? ` (${skippedCount} skipped)` : ""}
        {" • "}
        {plannedMin} min planned
      </p>

      <div
        className="meeting-tool-hide-scrollbar"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          paddingRight: 2,
        }}
      >
        {topics.length === 0 ? (
          <p style={{ color: "#64748b", fontSize: 13, textAlign: "center" }}>
            No topics planned for this session.
          </p>
        ) : (
          topics.map((topic, index) => {
            const segment = segments[index];
            const status = segment?.status || "pending";
            const isCurrent = status === "current";
            const isCompleted = status === "completed";
            const isSkipped = status === "skipped";
            const borderColor = isCompleted
              ? "rgba(34,197,94,0.45)"
              : isSkipped
                ? "rgba(245,158,11,0.45)"
                : isCurrent
                  ? "rgba(59,130,246,0.45)"
                  : "rgba(255,255,255,0.08)";

            return (
              <div
                key={topic.id}
                style={{
                  borderRadius: 12,
                  border: `1px solid ${borderColor}`,
                  background: "#12161f",
                  padding: 12,
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    {statusBadge(status, index)}
                    <p
                      style={{
                        margin: "8px 0 0",
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#fff",
                        wordBreak: "break-word",
                      }}
                    >
                      {topic.name}
                    </p>
                    <p
                      style={{
                        margin: "4px 0 0",
                        fontSize: 11,
                        color: "#94a3b8",
                        lineHeight: 1.4,
                      }}
                    >
                      {topic.timelineStart} - {topic.timelineEnd}
                      <span style={{ margin: "0 6px", opacity: 0.5 }}>·</span>
                      Planned: {topic.durationMin} min
                      {(() => {
                        const actualSec = resolveActualDurationSec(topic);
                        if (actualSec == null) return null;
                        return (
                          <>
                            <span style={{ margin: "0 6px", opacity: 0.5 }}>
                              ·
                            </span>
                            Actual: {formatActualDurationDisplay(actualSec)}
                          </>
                        );
                      })()}
                      {topic.actualStartAt || topic.actualEndAt ? (
                        <span style={{ display: "block", marginTop: 2 }}>
                          {topic.actualStartAt
                            ? new Date(topic.actualStartAt).toLocaleTimeString(
                                [],
                                { hour: "numeric", minute: "2-digit" },
                              )
                            : "-"}
                          {" -> "}
                          {topic.actualEndAt
                            ? new Date(topic.actualEndAt).toLocaleTimeString(
                                [],
                                { hour: "numeric", minute: "2-digit" },
                              )
                            : "..."}
                        </span>
                      ) : null}
                    </p>
                  </div>

                  {isCompleted || isSkipped ? (
                    <button
                      type="button"
                      disabled
                      style={{
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        border: "none",
                        borderRadius: 8,
                        padding: "8px 10px",
                        background: isSkipped
                          ? "rgba(245,158,11,0.18)"
                          : "rgba(34,197,94,0.18)",
                        color: isSkipped ? "#fbbf24" : "#4ade80",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "default",
                      }}
                    >
                      <Check size={14} />
                      {isSkipped ? "Skipped" : "Completed"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={!isCurrent}
                      onClick={() => {
                        if (isCurrent) onMarkCompleted();
                      }}
                      style={{
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        border: "1px solid rgba(255,255,255,0.14)",
                        borderRadius: 8,
                        padding: "8px 10px",
                        background: isCurrent
                          ? "rgba(255,255,255,0.06)"
                          : "transparent",
                        color: isCurrent ? "#e2e8f0" : "#64748b",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: isCurrent ? "pointer" : "not-allowed",
                        opacity: isCurrent ? 1 : 0.55,
                      }}
                    >
                      <Circle size={12} />
                      {isCurrent && currentSubtopicName ? "Complete subtopic" : "Mark completed"}
                    </button>
                  )}
                </div>

                {/* Subtopics list */}
                {topic.subtopics && topic.subtopics.length > 0 && (
                  <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                    {topic.subtopics.map((sub, si) => (
                      <div
                        key={sub.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 10px",
                          borderRadius: 8,
                          background: sub.completed
                            ? "rgba(34,197,94,0.08)"
                            : "rgba(255,255,255,0.03)",
                          border: `1px solid ${sub.completed ? "rgba(34,197,94,0.25)" : "rgba(255,255,255,0.06)"}`,
                        }}
                      >
                        <span style={{ fontSize: 12, color: sub.completed ? "#6ee7b7" : "#cbd5e1" }}>
                          {sub.name}
                        </span>
                        <span style={{ fontSize: 11, color: sub.completed ? "#6ee7b7" : "#94a3b8", fontWeight: 600, whiteSpace: "nowrap" }}>
                          {sub.completed ? "Done" : `${sub.durationMin}m`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {isCurrent ? (
                  <div
                    style={{
                      marginTop: 12,
                      borderRadius: 10,
                      border: "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(0,0,0,0.28)",
                      padding: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        marginBottom: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          borderRadius: 6,
                          padding: "3px 8px",
                          fontSize: 9,
                          fontWeight: 800,
                          letterSpacing: 0.4,
                          background: "rgba(148,163,184,0.15)",
                          color: "#94a3b8",
                        }}
                      >
                        {currentSubtopicName ? "ACTIVE SUBTOPIC" : "ACTIVE TOPIC"}
                      </span>
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#e2e8f0",
                          flexWrap: "wrap",
                        }}
                      >
                        {currentSubtopicName ? (
                          <>
                            <span>
                              {subtopicElapsedLabel || "0:00"} / {formatTopicElapsedLabel(subtopicTimeLimitSec)}
                            </span>
                            <span style={{ color: "#60a5fa" }}>
                              {subtopicProgressPercent}% of subtopic
                            </span>
                          </>
                        ) : (
                          <>
                            <span>
                              {topicElapsedLabel} / {limitLabel}
                            </span>
                            <span style={{ color: "#60a5fa" }}>
                              {topicProgressPercent}% of topic
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {currentSubtopicName && (
                      <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>
                        {currentSubtopicName}
                      </p>
                    )}

                    <div
                      style={{
                        height: 10,
                        borderRadius: 999,
                        overflow: "hidden",
                        background: "rgba(255,255,255,0.1)",
                        marginBottom: 8,
                      }}
                      title={(currentSubtopicName || activeTopicName) + " - " + String(currentSubtopicName ? subtopicProgressPercent : currentFill) + "%"}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: String(currentSubtopicName ? subtopicProgressPercent : currentFill) + "%",
                          borderRadius: 999,
                          background: "#3b82f6",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 10,
                        color: "#64748b",
                      }}
                    >
                      {currentSubtopicName ? "Progress for this subtopic only" : "Progress for this topic only"}
                    </p>
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
